import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  Box,
  Eye,
  EyeOff,
  Layers,
  Link2,
  Loader2,
  Plus,
  Rocket,
  RotateCcw,
  Save,
  Trash2,
  Unlink,
  Wand2,
} from "lucide-react";

import { ApiError } from "@/api/client";
import {
  useDetachPoolWorkload,
  useDiscardPoolDraftWorkload,
  useInheritPoolWorkload,
  usePoolWorkload,
  usePromotePoolDraftWorkload,
  useSavePoolDraftWorkload,
} from "@/api/local/pools";
import { useRollouts } from "@/api/local/rollout";
import {
  diffWorkloadSpec,
  flattenArgs,
  hashWorkloadSpec,
  normalizeWorkloadSpec,
  type WorkloadArg,
  type WorkloadEnvVar,
  type WorkloadSpec,
} from "@/api/local/workload";
import type { Pool, PoolWorkloadOut, Service } from "@/api/types";
import { useConfirm } from "@/components/ConfirmDialog";
import { RegistryAuthIndicator } from "@/components/RegistryAuthIndicator";
import { Pill } from "@/components/Pill";
import { RolloutModal } from "@/components/RolloutModal";
import { Section } from "@/components/Section";
import { computeTargetReplicas } from "@/pages/workloads/pool/PoolReplicas";

interface Props {
  pool: Pool;
  service: Service;
}

/**
 * Pool Workload page.
 *
 * A pool can either *inherit* the service-level workload verbatim
 * (the common case — the same model on every hardware tier) or *own*
 * its workload outright (TTS-style: bf16 on H100/A100, int4 on L4).
 * The page surfaces both modes:
 *
 * * **Inherit** — read-only summary of `Service.workload`, with a
 *   "Override per-pool" button that calls `/detach`. The controller
 *   seeds `Pool.workload_draft` with a deep copy of the service spec
 *   so the editor opens with somewhere to start.
 * * **Own** — full WorkloadSpec editor (image / command / args / env /
 *   port / probe) bound to `Pool.workload_draft`. Save / Discard
 *   mirror the service-level draft protocol; the Roll out button
 *   triggers a *pool*-scoped blue/green rollout that promotes
 *   `workload_draft → workload`.
 *
 * Detaching does not change runtime behaviour until the operator
 * rolls out — until then the pool keeps running on the inherited
 * spec. Inheriting clears both the live and draft pool specs and
 * the reconciler picks the change up next tick.
 */
export function PoolWorkload({ pool, service }: Props) {
  const rollouts = useRollouts();
  const queryClient = useQueryClient();
  const stateQuery = usePoolWorkload(pool.id);

  if (stateQuery.isLoading || !stateQuery.data) {
    return (
      <Section title="Workload" icon={<Box />}>
        <p className="muted" style={{ fontSize: 12 }}>
          <Loader2
            style={{
              width: 12,
              height: 12,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          loading…
        </p>
      </Section>
    );
  }

  return (
    <PoolWorkloadInner
      pool={pool}
      service={service}
      state={stateQuery.data}
      activeRolloutId={
        rollouts.active &&
        rollouts.active.scope === "pool" &&
        rollouts.active.pool_id === pool.id
          ? rollouts.active.id
          : null
      }
      activeRolloutState={
        rollouts.active &&
        rollouts.active.scope === "pool" &&
        rollouts.active.pool_id === pool.id
          ? rollouts.active.state
          : null
      }
      onRolloutComplete={() => {
        queryClient.invalidateQueries({ queryKey: ["pools", pool.id, "workload"] });
        queryClient.invalidateQueries({ queryKey: ["pools", "all"] });
      }}
    />
  );
}

interface InnerProps {
  pool: Pool;
  service: Service;
  state: PoolWorkloadOut;
  activeRolloutId: number | null;
  activeRolloutState: string | null;
  onRolloutComplete: () => void;
}

function PoolWorkloadInner({
  pool,
  service,
  state,
  activeRolloutId,
  activeRolloutState,
  onRolloutComplete,
}: InnerProps) {
  const confirm = useConfirm();
  const isOwn = state.mode === "own";
  const persistedDraft = useMemo(
    () => normalizeWorkloadSpec(state.draft),
    [state.draft],
  );
  const persistedLive = useMemo(
    () => normalizeWorkloadSpec(state.live),
    [state.live],
  );

  const [draft, setDraft] = useState<WorkloadSpec>(persistedDraft);
  const [pristineHash, setPristineHash] = useState(() =>
    hashWorkloadSpec(persistedDraft),
  );
  const [pristineRevision, setPristineRevision] = useState(state.draft_revision);
  const [conflict, setConflict] = useState(false);
  const dirty = isOwn && hashWorkloadSpec(draft) !== pristineHash;
  useEffect(() => {
    // A background refetch (topbar auto-refresh timer, a teammate's
    // save, a rollout completing) moves the persisted draft/revision and
    // re-fires this effect. Do NOT let it clobber in-progress edits or
    // tear down a live conflict banner: only reseed from the server when
    // the editor is clean AND no conflict is being resolved. `dirty` /
    // `conflict` are read from the current render's closure on purpose
    // (not deps — the reseed-on-save handlers clear `dirty` so the next
    // refetch reseeds cleanly).
    if (dirty || conflict) return;
    setDraft(persistedDraft);
    setPristineHash(hashWorkloadSpec(persistedDraft));
    setPristineRevision(state.draft_revision);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedDraft, state.draft_revision]);
  const pendingDiffs = useMemo(
    () => (isOwn ? diffWorkloadSpec(persistedLive, persistedDraft) : []),
    [isOwn, persistedLive, persistedDraft],
  );

  const queryClient = useQueryClient();
  const saveMutation = useSavePoolDraftWorkload(pool.id, pool.service_id);
  const discardMutation = useDiscardPoolDraftWorkload(pool.id, pool.service_id);
  const detachMutation = useDetachPoolWorkload(pool.id, pool.service_id);
  const inheritMutation = useInheritPoolWorkload(pool.id, pool.service_id);
  const promoteMutation = usePromotePoolDraftWorkload(pool.id, pool.service_id);

  const [showRollout, setShowRollout] = useState(false);
  // Progress for an active pool rollout is rendered inline on the
  // pool's Replicas tab via `PoolRolloutPanel`. We deliberately do not
  // auto-open this modal — the "view rollout" button below keeps it
  // available on demand without ambushing the operator with a
  // full-screen progress dialog.

  const save = () => {
    setConflict(false);
    saveMutation.mutate(
      { spec: draft, ifRevision: pristineRevision },
      {
        onSuccess: () => {
          // Mark the just-saved buffer as the new pristine baseline so
          // `dirty` clears immediately; the revision is refreshed by the
          // reseed effect once the invalidated workload query refetches.
          setPristineHash(hashWorkloadSpec(draft));
          setConflict(false);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 412) {
            setConflict(true);
            // The reseed effect preserves the buffer + banner (guarded
            // by `conflict`) when the refetch below lands.
            queryClient.invalidateQueries({
              queryKey: ["pools", pool.id, "workload"],
            });
          }
        },
      },
    );
  };

  const reseedFromServer = () => {
    setDraft(persistedDraft);
    setPristineHash(hashWorkloadSpec(persistedDraft));
    setPristineRevision(state.draft_revision);
    setConflict(false);
  };

  const isRolloutActive = activeRolloutId !== null;
  // Pool-scoped rollouts can roll on a pool that owns its workload
  // even when there is no pending diff — that's a re-roll. We still
  // forbid rolling when there are unsaved local edits (operator
  // shouldn't accidentally ship an in-flight typo).
  const canRollout = isOwn && !isRolloutActive && !dirty;

  const planTarget = useMemo(() => persistedDraft, [persistedDraft]);
  const planSource = useMemo(
    () => (state.live && Object.keys(state.live as object).length > 0 ? persistedLive : null),
    [persistedLive, state.live],
  );
  const plan = useMemo(
    () => [
      {
        pool_id: pool.id,
        pool_name: pool.sky_service_name,
        replicas: computeTargetReplicas(pool, null),
        target: planTarget,
      },
    ],
    [pool, planTarget],
  );

  return (
    <Section
      title="Workload"
      icon={<Box />}
      count={
        <span style={{ display: "inline-flex", gap: 6 }}>
          <Pill tone={isOwn ? "warn" : "neutral"}>
            {isOwn ? "own" : "inherit"}
          </Pill>
          {isOwn && pendingDiffs.length > 0 && (
            <Pill tone="warn">
              {pendingDiffs.length} pending change
              {pendingDiffs.length === 1 ? "" : "s"}
            </Pill>
          )}
          {isRolloutActive && activeRolloutState && (
            <Pill tone="info" pulse>
              rollout {activeRolloutState}
            </Pill>
          )}
        </span>
      }
      actions={
        <div className="toolbar" style={{ gap: 6 }}>
          {!isOwn && (
            <button
              className="btn btn--primary"
              onClick={() => detachMutation.mutate()}
              disabled={detachMutation.isPending}
              title="Detach this pool from the service workload — start editing a pool-scoped spec"
            >
              {detachMutation.isPending ? <Loader2 /> : <Unlink />}
              override per-pool
            </button>
          )}
          {isOwn && (
            <>
              <button
                className="btn btn--ghost"
                onClick={async () => {
                  if (pendingDiffs.length > 0) {
                    const ok = await confirm({
                      title: "Discard pending changes?",
                      tone: "warn",
                      confirmLabel: "Discard",
                      description:
                        "Discard pool-scoped pending changes and revert the editor to the live spec?",
                    });
                    if (!ok) return;
                  }
                  discardMutation.mutate(undefined, {
                    onSuccess: () => {
                      // Clear local edits so the reseed effect isn't
                      // blocked by a stale `dirty` after the revert.
                      setDraft(persistedLive);
                      setPristineHash(hashWorkloadSpec(persistedLive));
                      setConflict(false);
                    },
                  });
                }}
                disabled={discardMutation.isPending || (pendingDiffs.length === 0 && !dirty)}
                title="Reset draft to live (does not revert to inherit)"
              >
                {discardMutation.isPending ? <Loader2 /> : <RotateCcw />}
                discard
              </button>
              <button
                className="btn"
                onClick={save}
                disabled={!dirty || saveMutation.isPending}
              >
                {saveMutation.isPending ? <Loader2 /> : <Save />}
                {saveMutation.isPending
                  ? "saving…"
                  : dirty
                    ? "save draft"
                    : "saved"}
              </button>
              <button
                className="btn btn--primary"
                disabled={!canRollout && !isRolloutActive}
                onClick={() => setShowRollout(true)}
                title={
                  dirty
                    ? "Save your draft before rolling out"
                    : "Promote the pool draft to live with a blue/green rollout"
                }
              >
                <Rocket />
                {isRolloutActive
                  ? "view rollout"
                  : pendingDiffs.length === 0
                    ? "re-roll"
                    : "Roll out…"}
              </button>
              <button
                className="btn btn--ghost"
                onClick={async () => {
                  const ok = await confirm({
                    title: "Revert to inherit Service.workload?",
                    tone: "warn",
                    confirmLabel: "Revert to inherit",
                    description:
                      "Pool-scoped live + draft will be cleared and the reconciler will roll the pool over to the service spec.",
                  });
                  if (!ok) return;
                  inheritMutation.mutate();
                }}
                disabled={inheritMutation.isPending || isRolloutActive}
                title="Drop pool-scoped workload and re-inherit Service.workload"
              >
                {inheritMutation.isPending ? <Loader2 /> : <Link2 />}
                inherit again
              </button>
            </>
          )}
        </div>
      }
    >
      {!isOwn && (
        <div className="flash flash--info" style={{ marginBottom: 16 }}>
          <Layers
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          This pool is <strong>inheriting</strong> the workload from{" "}
          <code>{service.name}</code>. Replicas run whatever the service&rsquo;s
          live spec says — typically the right call for a homogeneous service
          where every hardware tier serves the same model. Click{" "}
          <strong>override per-pool</strong> to detach: the controller copies
          the current service spec into a pool-scoped draft and switches the
          editor below to <em>own</em> mode. Detaching does not change runtime
          until you save + roll the pool draft.
        </div>
      )}

      {isOwn && (
        <div className="flash" style={{ marginBottom: 16 }}>
          <Unlink
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          This pool <strong>owns</strong> its workload — service-scope rollouts
          will <em>skip</em> it. Roll outs from this page promote{" "}
          <code>workload_draft</code> &rarr; <code>workload</code> for this
          pool only. Use <strong>inherit again</strong> to drop the pool spec
          and follow <code>{service.name}</code> once more.
        </div>
      )}

      {dirty && (
        <div className="flash" style={{ marginBottom: 16 }}>
          You have unsaved edits. Click <strong>save draft</strong> to persist
          them on the controller, or <strong>discard</strong> to revert.
        </div>
      )}

      {conflict && (
        <div className="flash flash--warn" style={{ marginBottom: 16 }}>
          <AlertTriangle
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          The pool draft was modified somewhere else (a teammate saved, or a
          rollout promoted it) while you were editing. Your unsaved edits are
          still in this tab — review and either{" "}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={reseedFromServer}
            style={{ display: "inline-flex" }}
          >
            <RotateCcw />
            load latest
          </button>{" "}
          (discards your edits) or{" "}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              // Force-save saves the LOCAL buffer (`draft` — "mine"),
              // dropping the If-Match guard so the PUT wins.
              setConflict(false);
              saveMutation.mutate(
                { spec: draft },
                {
                  onSuccess: () => {
                    setPristineHash(hashWorkloadSpec(draft));
                    setConflict(false);
                  },
                },
              );
            }}
            style={{ display: "inline-flex" }}
          >
            <Save />
            force-save mine
          </button>
          .
        </div>
      )}

      {saveMutation.isError && !conflict && (
        <div className="flash flash--info" style={{ marginBottom: 16 }}>
          <AlertTriangle
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          Save failed:{" "}
          {saveMutation.error instanceof Error
            ? saveMutation.error.message
            : "unknown error"}
          . Your edits are still in this tab — fix the issue and retry.
        </div>
      )}

      {promoteMutation.isError && (
        <div className="flash flash--info" style={{ marginBottom: 16 }}>
          Promote failed:{" "}
          {promoteMutation.error instanceof Error
            ? promoteMutation.error.message
            : "unknown error"}
        </div>
      )}

      <DockerEditor value={draft} readOnly={!isOwn} onChange={setDraft} />

      <p className="muted" style={{ fontSize: 11.5, marginTop: 16 }}>
        {isOwn ? (
          <>
            Pool-scoped workload edits do not affect other pools. Rolling out
            from this page only re-deploys this pool — the rest of{" "}
            <code>{service.name}</code> keeps running on whatever spec they
            were last rolled with (inherited service spec, or their own).
          </>
        ) : (
          <>
            The fields below mirror <code>{service.name}</code>&rsquo;s live
            workload. To customise just this pool, click{" "}
            <strong>override per-pool</strong> above; the controller copies
            this spec into a pool-scoped draft and unlocks the editor.
          </>
        )}
      </p>

      <RolloutModal
        open={showRollout && isOwn}
        onClose={() => setShowRollout(false)}
        scope="pool"
        scopeLabel={pool.sky_service_name}
        serviceId={pool.service_id}
        poolId={pool.id}
        source={planSource}
        target={planTarget}
        plan={plan}
        firstDeploy={false}
        onComplete={onRolloutComplete}
      />
    </Section>
  );
}

interface DockerEditorProps {
  value: WorkloadSpec;
  readOnly: boolean;
  onChange: (next: WorkloadSpec) => void;
}

/**
 * Same shape as the Service-level DockerEditor but accepts a
 * ``readOnly`` flag so an inherit pool can still see what would run
 * (the inherited service spec) without offering edit affordances.
 */
function DockerEditor({ value, readOnly, onChange }: DockerEditorProps) {
  return (
    <>
      <SubBlock title="Image" icon={<Box />}>
        <div className="form-grid">
          <label>image</label>
          <div className="field">
            <input
              value={value.image ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                onChange({
                  ...value,
                  image: e.target.value === "" ? null : e.target.value,
                })
              }
              placeholder="ghcr.io/spiral-ai/backstage-loadtest:latest"
            />
            <RegistryAuthIndicator image={value.image} />
            <div className="field-hint">
              Docker image SkyPilot pulls onto every replica. Defaults
              to the backstage loadtest image; per-pool overrides are
              the canonical home for hardware-tier-specific images
              (e.g. <code>vllm:bf16-h100</code> on the H100 tier and{" "}
              <code>vllm:int4-l4</code> on the L4 tier under the same
              service).
            </div>
            <div
              className="field-hint"
              style={{ color: "var(--color-warn, #b25b00)" }}
            >
              <strong>Requirements:</strong> the image MUST include{" "}
              <code>sshd</code> (openssh-server) <em>and</em> a POSIX{" "}
              shell. SkyPilot opens an SSH session into the running
              container to install its runtime and exec{" "}
              <code>setup</code>/<code>run</code>; minimal images
              (Alpine + node only, scratch, distroless) fail with{" "}
              <em>“Failed to SSH to …”</em>. RunPod is especially
              strict — its pod-is-a-container model SSHes into the
              workload image directly. Safe bases:{" "}
              <code>runpod/base:1.0.3-ubuntu2404</code>, or FROM{" "}
              <code>ubuntu:22.04</code> + <code>openssh-server</code>.
              Pin a fully-qualified tag — RunPod scrubs old series
              (the <code>0.6.x</code> family no longer resolves);
              the controller probes the registry on save and a
              periodic sweep re-flags pods whose image gets yanked
              afterwards.
            </div>
          </div>
          <label>command</label>
          <div className="field">
            <input
              value={value.command ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                onChange({
                  ...value,
                  command: e.target.value === "" ? null : e.target.value,
                })
              }
              placeholder="/usr/local/bin/loadtest-server"
            />
            <div className="field-hint">
              Entrypoint command. SkyPilot&rsquo;s docker runtime bypasses the
              image&rsquo;s built-in <code>CMD</code> — leave this empty only
              if the container should sleep forever. Default loadtest image
              uses <code>/usr/local/bin/loadtest-server</code>; vLLM
              workloads run{" "}
              <code>python -m vllm.entrypoints.openai.api_server</code>.
            </div>
          </div>
          <label>port</label>
          <div className="field">
            <input
              type="number"
              value={value.port}
              disabled={readOnly}
              onChange={(e) =>
                onChange({ ...value, port: Number(e.target.value) })
              }
            />
          </div>
        </div>
      </SubBlock>

      <SubBlock title="Args" icon={<Wand2 />}>
        <ArgsEditor
          args={value.args}
          readOnly={readOnly}
          onChange={(next) => onChange({ ...value, args: next })}
          flagPlaceholder="--model"
          valuePlaceholder="meta-llama/Llama-3-70B-Instruct"
        />
        <p className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          One row per <code>--flag value</code>. Boolean flags leave the value
          empty.
        </p>
      </SubBlock>

      <SubBlock title="Env" icon={<Box />}>
        <EnvEditor
          env={value.env}
          readOnly={readOnly}
          onChange={(next) => onChange({ ...value, env: next })}
        />
      </SubBlock>

      <SubBlock title="Probe" icon={<Activity />}>
        <div className="form-grid">
          <label>health path</label>
          <div className="field">
            <input
              value={value.health_path}
              disabled={readOnly}
              onChange={(e) =>
                onChange({ ...value, health_path: e.target.value })
              }
              placeholder="/healthz"
            />
          </div>
          <label>initial delay (s)</label>
          <div className="field">
            <input
              type="number"
              min={0}
              value={value.initial_delay_seconds}
              disabled={readOnly}
              onChange={(e) =>
                onChange({
                  ...value,
                  initial_delay_seconds: Number(e.target.value),
                })
              }
              style={{ width: 120 }}
            />
          </div>
          <label>metrics path</label>
          <div className="field">
            <input
              value={value.metrics_path}
              disabled={readOnly}
              onChange={(e) =>
                onChange({ ...value, metrics_path: e.target.value })
              }
              placeholder="/metrics"
            />
            <div className="field-hint">
              Prometheus exposition path; the port is shared with{" "}
              <code>port</code> above. Scrape target is{" "}
              <code>
                http://&lt;replica&gt;:&lt;port&gt;{value.metrics_path}
              </code>
              .
            </div>
          </div>
        </div>
      </SubBlock>

      <ArgvPreview workload={value} />
    </>
  );
}

function SubBlock({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="subblock">
      <div className="subblock__title">
        {icon}
        <span>{title}</span>
      </div>
      <div className="subblock__body">{children}</div>
    </div>
  );
}

interface ArgsEditorProps {
  args: WorkloadArg[];
  readOnly: boolean;
  onChange: (next: WorkloadArg[]) => void;
  flagPlaceholder?: string;
  valuePlaceholder?: string;
}

function ArgsEditor({
  args,
  readOnly,
  onChange,
  flagPlaceholder,
  valuePlaceholder,
}: ArgsEditorProps) {
  const update = (idx: number, patch: Partial<WorkloadArg>) => {
    const next = args.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  return (
    <div className="argrows">
      {args.map((a, idx) => (
        <div key={idx} className="argrow">
          <input
            className="argrow__flag"
            value={a.flag}
            disabled={readOnly}
            placeholder={flagPlaceholder ?? "--flag"}
            onChange={(e) => update(idx, { flag: e.target.value })}
          />
          <input
            className="argrow__value"
            value={a.value ?? ""}
            disabled={readOnly}
            placeholder={valuePlaceholder ?? "value (optional)"}
            onChange={(e) => update(idx, { value: e.target.value })}
          />
          {!readOnly && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => onChange(args.filter((_, i) => i !== idx))}
              aria-label="remove"
            >
              <Trash2 />
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => onChange([...args, { flag: "", value: "" }])}
        >
          <Plus />
          add arg
        </button>
      )}
    </div>
  );
}

function EnvEditor({
  env,
  readOnly,
  onChange,
}: {
  env: WorkloadEnvVar[];
  readOnly: boolean;
  onChange: (next: WorkloadEnvVar[]) => void;
}) {
  const [revealed, setRevealed] = useState<Set<number>>(new Set());

  const toggleReveal = (idx: number) => {
    const next = new Set(revealed);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setRevealed(next);
  };

  return (
    <div className="argrows">
      {env.map((e, idx) => {
        const isSecret = !!e.secret;
        const show = !isSecret || revealed.has(idx);
        return (
          <div key={idx} className="argrow">
            <input
              className="argrow__flag"
              value={e.key}
              disabled={readOnly}
              placeholder="KEY"
              onChange={(ev) => {
                const next = env.slice();
                next[idx] = { ...e, key: ev.target.value };
                onChange(next);
              }}
            />
            <input
              className="argrow__value"
              type={show ? "text" : "password"}
              value={e.value}
              disabled={readOnly}
              placeholder="value"
              onChange={(ev) => {
                const next = env.slice();
                next[idx] = { ...e, value: ev.target.value };
                onChange(next);
              }}
            />
            <label
              className="muted"
              style={{
                display: "inline-flex",
                gap: 4,
                alignItems: "center",
                fontSize: 10.5,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
              }}
            >
              <input
                type="checkbox"
                checked={isSecret}
                disabled={readOnly}
                onChange={(ev) => {
                  const next = env.slice();
                  next[idx] = { ...e, secret: ev.target.checked };
                  onChange(next);
                }}
              />
              secret
            </label>
            {isSecret && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => toggleReveal(idx)}
                aria-label="reveal"
              >
                {show ? <EyeOff /> : <Eye />}
              </button>
            )}
            {!readOnly && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => onChange(env.filter((_, i) => i !== idx))}
                aria-label="remove"
              >
                <Trash2 />
              </button>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => onChange([...env, { key: "", value: "" }])}
        >
          <Plus />
          add env var
        </button>
      )}
    </div>
  );
}

function ArgvPreview({ workload }: { workload: WorkloadSpec }) {
  const argv: string[] = [];
  if (workload.command) argv.push(workload.command);
  argv.push(...flattenArgs(workload.args));
  return (
    <div className="subblock">
      <div className="subblock__title">
        <Wand2 />
        <span>Rendered argv</span>
      </div>
      <pre className="code" style={{ marginTop: 0 }}>
        {workload.image ?? "(no image)"}
        {"\n  "}
        {argv.length === 0 ? "(no args)" : argv.join(" ")}
      </pre>
      <p className="muted" style={{ fontSize: 11, marginTop: 8 }}>
        Preview of what the controller will hand to SkyServe on every replica
        of this pool. Args are appended to the command verbatim.
      </p>
    </div>
  );
}
